import * as React from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  Box,
  Modal,
  Button,
  HeadingText,
  HeadingBar,
  ParagraphText,
  StackLayout,
  StatusBanner,
} from '@leagueplatform/genesis-core';
import type { EnvironmentData } from '@web-config-app/core-react';
import { useFormatEntityTableData } from '@web-config-app/core-react';
import type { TreeNode } from '@web-config-app/core';
import type { SyncModeConfig } from './sync-mode.types';
import { downloadEntityDataCSV } from './download-entity-data-csv';
import {
  EntityDataTable,
  isMultiEnvironmentRow,
} from '../entity-data-table/entity-data-table.component';
import { SyncDirectionBar } from './sync-direction-bar.component';

export interface EntityDataTableModalProps {
  currentEnvironmentData: EnvironmentData;
  otherEnvironmentData?: EnvironmentData;
  entityName: string | undefined;
  dataPath?: string;
  entityTreeNodeMap: Map<string, TreeNode>;
  onOpenChange: (open: boolean) => void;
  syncMode?: SyncModeConfig;
}

export const EntityDataTableModalContents = ({
  currentEnvironmentData,
  otherEnvironmentData,
  entityTreeNodeMap,
  entityName,
  dataPath,
  onOpenChange,
  syncMode,
}: EntityDataTableModalProps) => {
  const { formatMessage } = useIntl();
  const { rows, columnHeaders, isMultiEnvironment } = useFormatEntityTableData({
    currentEnvironmentData,
    otherEnvironmentData,
    dataPath,
    entityTreeNodeMap,
  });

  const envDataDifferenceCount = React.useMemo(() => {
    if (!isMultiEnvironment) return 0;
    const multiEnvRows = rows.filter(isMultiEnvironmentRow);
    return multiEnvRows.filter((row) => row.data !== row.otherData).length;
  }, [rows, isMultiEnvironment]);

  const handleSyncConfirm = React.useCallback(() => {
    syncMode?.onSyncConfirm();
    onOpenChange(false);
  }, [syncMode, onOpenChange]);

  const title = syncMode
    ? formatMessage({ id: 'SYNC_ENTITY' }, { entityName })
    : formatMessage({ id: 'VIEW_ALL_DATA' });

  const subtitle = syncMode
    ? formatMessage(
        { id: 'ENTITY_DATA_SYNC_TABLE_CAPTION' },
        {
          entityName,
          targetEnvironment: syncMode.targetEnvironment.name,
          sourceEnvironment: syncMode.sourceEnvironment.name,
        },
      )
    : undefined;

  return (
    <>
      <HeadingBar
        leadingAction={
          !syncMode && (
            <Button
              priority="secondary"
              quiet
              icon="interfaceDownload"
              size="medium"
              onClick={() =>
                downloadEntityDataCSV({
                  filename: entityName ?? 'Untitled',
                  data: rows,
                  columnHeaders,
                })
              }
            >
              {formatMessage({ id: 'DOWNLOAD_AS_CSV' })}
            </Button>
          )
        }
        trailingAction={
          <Modal.Close>
            <Button
              priority="tertiary"
              quiet
              icon="tinyClose"
              hideLabel
              size="small"
            >
              {formatMessage({ id: 'CLOSE' })}
            </Button>
          </Modal.Close>
        }
        title={
          <Modal.Title>
            <HeadingText level="1" size="xl">
              {title}
            </HeadingText>
          </Modal.Title>
        }
      />
      {syncMode && (
        <SyncDirectionBar
          sourceEnvironment={syncMode.sourceEnvironment}
          targetEnvironment={syncMode.targetEnvironment}
        />
      )}
      <Box
        css={{
          padding: syncMode ? '$half $oneAndHalf' : '$half',
          borderColor: '$onSurfaceBorderSubdued',
          borderWidth: 0,
          borderBlockStartWidth: '$thin',
          borderStyle: 'solid',
          overflowX: 'auto',
          width: '100%',
        }}
      >
        <EntityDataTable
          rows={rows}
          columnHeaders={columnHeaders}
          envDataDifferenceCount={envDataDifferenceCount}
          isMultiEnvironment={isMultiEnvironment}
          syncMode={syncMode}
        />
      </Box>
      {syncMode && (
        <>
          {envDataDifferenceCount > 0 && (
            <StatusBanner
              status="warning"
              title={subtitle}
              css={{ marginInline: '$oneAndHalf' }}
            >
              <ParagraphText>
                {formatMessage(
                  { id: 'SYNC_OVERWRITE_WARNING' },
                  {
                    count: envDataDifferenceCount,
                    targetEnvironment: syncMode.targetEnvironment.name,
                  },
                )}
              </ParagraphText>
            </StatusBanner>
          )}
          <StackLayout
            orientation="horizontal"
            horizontalAlignment="end"
            spacing="$one"
            css={{ padding: '$half $oneAndHalf' }}
          >
            <Modal.Close>
              <Button priority="tertiary" quiet>
                {formatMessage({ id: 'CANCEL' })}
              </Button>
            </Modal.Close>
            <Button
              onClick={handleSyncConfirm}
              disabled={envDataDifferenceCount === 0}
            >
              {formatMessage(
                { id: 'SYNC_TO_TARGET' },
                { targetEnvironment: syncMode.targetEnvironment.name },
              )}
            </Button>
          </StackLayout>
        </>
      )}
    </>
  );
};
